import {
  InputLabel,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@suraasa/placebo-ui"
import api from "api"
import React, { useEffect, useRef, useState } from "react"
import {
  convertToAnnual,
  formatNumberWithCommas,
  pluralize,
} from "utils/helpers"
import { useUserLeadContext } from "views/leadGrowthForm/Context"

const SalaryCalculator = () => {
  const { leadProfile } = useUserLeadContext()
  const [numberOfYears, setNumberOfYears] = useState(10)
  const [appraisalMode, setAppraisalMode] = useState(false)

  const [currentSalary, setCurrentSalary] = useState(
    leadProfile ? Number(leadProfile.currentSalary) / 12 : ""
  )

  const [targetSalary, setTargetSalary] = useState(
    leadProfile ? Number(leadProfile.desiredSalary) / 12 : ""
  )
  const [targetSalaryCurrency, setTargetSalaryCurrency] = useState(
    leadProfile ? leadProfile.desiredSalaryCurrency : ""
  )

  const [currencies, setCurrencies] = useState<
    { label: string; value: string }[]
  >([])

  const targetSalaryYearly = convertToAnnual(Number(targetSalary))
  const currentSalaryYearly = convertToAnnual(Number(currentSalary))
  const graphAreaRef = useRef<HTMLDivElement>(null)
  const [graphAreaWidth, setGraphAreaWidth] = useState(0)

  useEffect(() => {
    if (graphAreaRef.current) {
      setGraphAreaWidth(graphAreaRef.current.offsetWidth)
    }

    const handleResize = () => {
      if (graphAreaRef.current) {
        setGraphAreaWidth(graphAreaRef.current.offsetWidth)
      }
    }

    window.addEventListener("resize", handleResize)

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const listCurrencies = await api.global.currency.listCurrencies({
          params: { page: -1 },
        })
        setCurrencies(
          listCurrencies.map(c => ({
            value: c.code,
            label: `${c.code} (${c.symbol})`,
          }))
        )
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }
    fetchCurrencies()
  }, [])

  useEffect(() => {
    if (leadProfile?.desiredSalaryCurrency) {
      setTargetSalaryCurrency(leadProfile.desiredSalaryCurrency)
    }
  }, [leadProfile?.desiredSalaryCurrency])

  const calculateTotalEarned = (salary: number, rate: number) => {
    return salary * ((Math.pow(1 + rate, numberOfYears) - 1) / rate)
  }

  const withITQSalary = appraisalMode
    ? calculateTotalEarned(targetSalaryYearly, 7.5 / 100)
    : targetSalaryYearly * numberOfYears
  const withoutITQSalary = appraisalMode
    ? calculateTotalEarned(currentSalaryYearly, 2.5 / 100)
    : currentSalaryYearly * numberOfYears

  const baseWidth = graphAreaWidth * 0.2

  const getBarWidth = (salary: number) => {
    const scalingFactor = appraisalMode && numberOfYears != 1 ? 0.8 : 0.6
    const barWidth = (salary * scalingFactor) / 8000
    return Math.min(barWidth, graphAreaWidth - 80)
  }

  const withITQSalaryWidth = getBarWidth(targetSalaryYearly * numberOfYears)
  const withoutITQWidth = Math.min(
    getBarWidth(currentSalaryYearly * numberOfYears),
    graphAreaWidth * 0.8
  )

  function showTargetSalaryCurrency(): React.ReactNode {
    return (
      targetSalaryCurrency != null && targetSalary != 0 && targetSalaryCurrency
    )
  }

  return (
    <div>
      <Typography variant="title1" className="mb-3 text-onSurface-800">
        Cumulative Earnings Calculator
      </Typography>

      <div className="flex flex-wrap gap-2">
        <div className="flex w-full max-w-sm flex-col">
          <InputLabel label="Current Monthly Salary" />
          <div className="flex">
            <Select
              placeholder="INR (₹)"
              value={currencies.find(x => x.value === targetSalaryCurrency)}
              options={currencies}
              onChange={option => {
                setTargetSalaryCurrency(option?.value ?? null)
              }}
              className="!w-[200px] border-r-0 [&_div]:!rounded-r-none"
            />

            <TextField
              label=""
              placeholder="Ex - 1,00,000"
              value={currentSalary || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCurrentSalary(e.target.value)
              }}
              className="[&>div]:rounded-l-none [&>div]:border-l-0"
            />
          </div>
          <div className="h-0.5"></div>
          <Typography variant="smallBody" className="text-onSurface-500">
            {targetSalaryCurrency} {formatNumberWithCommas(currentSalaryYearly)}{" "}
            Annually
          </Typography>
        </div>
        <div className="flex w-full max-w-sm flex-col">
          <InputLabel label="Target Monthly Salary" />
          <div className="flex ">
            <Select
              placeholder="INR (₹)"
              value={currencies.find(x => x.value === targetSalaryCurrency)}
              options={currencies}
              onChange={option => {
                setTargetSalaryCurrency(option?.value ?? null)
              }}
              className="!w-[200px] border-r-0 [&_div]:!rounded-r-none "
            />

            <TextField
              label=""
              placeholder="Ex - 1,00,000"
              value={targetSalary || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTargetSalary(e.target.value)
              }}
              className="[&>div]:rounded-l-none [&>div]:border-l-0"
            />
          </div>
          <div className="h-0.5"></div>
          <Typography variant="smallBody" className="text-onSurface-500">
            {targetSalaryCurrency} {formatNumberWithCommas(targetSalaryYearly)}{" "}
            Annually
          </Typography>
        </div>
      </div>

      <div className="h-2"></div>
      <div className="rounded-t-xl border-2 border-[#E2E8F0] bg-onSurface-100 p-4">
        <div className="flex flex-row flex-wrap justify-between gap-2 sm:gap-0">
          <Typography variant="title3" className="!tracking-normal">
            Additional earnings of {targetSalaryCurrency}{" "}
            {withITQSalary - withoutITQSalary < 0
              ? 0
              : formatNumberWithCommas(withITQSalary - withoutITQSalary)}{" "}
            in next{" "}
            {pluralize(" year", numberOfYears, {
              plural: " years",
            })}
          </Typography>
          <div className="flex flex-row items-center gap-2">
            <Typography variant="preTitle" className="text-onSurface-500">
              With Appraisals
            </Typography>
            <Switch
              onClick={() => setAppraisalMode(x => !x)}
              checked={appraisalMode}
              className="data-[state=checked]:bg-success-500"
            />
          </div>
        </div>
        <div className="my-2 w-full">
          <div className="flex flex-row" ref={graphAreaRef}>
            <div className="h-[253px] w-0.25 bg-onSurface-300"></div>
            <div className="mb-4 mt-[52px] flex flex-col gap-4">
              <div className="flex flex-row items-center gap-1">
                <div className="relative">
                  <div
                    className="h-7 rounded-r-xl bg-success-500"
                    style={{
                      width: `${withITQSalaryWidth}px`,
                      transition: "width 0.5s ease-in-out",
                    }}
                  ></div>
                  {withITQSalaryWidth > baseWidth && (
                    <Typography
                      variant="title2"
                      className="absolute inset-y-1.75 right-2 text-white"
                    >
                      {showTargetSalaryCurrency()}{" "}
                      {formatNumberWithCommas(Number(withITQSalary.toFixed(0)))}
                    </Typography>
                  )}
                </div>
                <div className="flex flex-col">
                  {withITQSalaryWidth <= baseWidth && (
                    <Typography variant="strong">
                      {showTargetSalaryCurrency()}{" "}
                      {formatNumberWithCommas(Number(withITQSalary.toFixed(0)))}
                    </Typography>
                  )}
                  <Typography variant="smallBody">
                    With International Teaching Qualifications
                  </Typography>
                </div>
              </div>
              <div className="flex flex-row items-center gap-1">
                <div className="relative">
                  <div
                    className="h-7 rounded-r-xl bg-onSurface-300"
                    style={{
                      width: `${withoutITQWidth}px`,
                      transition: "width 0.5s ease-in-out",
                    }}
                  ></div>
                  {withoutITQWidth > baseWidth && (
                    <Typography
                      variant="title2"
                      className="absolute inset-y-1.75 right-2"
                    >
                      {showTargetSalaryCurrency()}{" "}
                      {currentSalary != 0 &&
                        formatNumberWithCommas(withoutITQSalary)}
                    </Typography>
                  )}
                </div>

                <div className="flex flex-col">
                  {withoutITQWidth <= baseWidth && (
                    <Typography variant="strong">
                      {showTargetSalaryCurrency()}{" "}
                      {currentSalary != 0 &&
                        formatNumberWithCommas(withoutITQSalary)}
                    </Typography>
                  )}
                  <Typography variant="smallBody">
                    Without International Teaching Qualifications
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          <div className="h-0.25 w-full bg-onSurface-300"></div>
          <div className="h-1"></div>
          <Typography
            variant="smallBody"
            className="text-onSurface-400 sm:w-[70%]"
          >
            Disclaimer: All these numbers are only for representation purposes.
            The final results and earnings may vary with individual performance
            in your school and the learning program that you enrol in.
          </Typography>
        </div>
      </div>

      <div className="rounded-b-xl border-x-2 border-b-2 border-[#E2E8F0] px-2 pb-6 pt-4">
        {" "}
        <div className="flex flex-row items-center justify-between ">
          <Typography variant="title3" className="text-onSurface-800">
            Tenure
          </Typography>
          <TextField
            value={numberOfYears}
            type="number"
            className="!w-12"
            endIcon={
              <Typography
                variant="smallBody"
                className=" !text-left !text-sm !font-normal !leading-5 text-onSurface-500"
              >
                years
              </Typography>
            }
            min={1}
            max={50}
            onChange={e => setNumberOfYears(Number(e.target.value))}
          />
        </div>
        <div className="h-2"></div>
        <Slider
          min={1}
          max={50}
          className="[&_span_span]:border-success-500 [&_span_span]:bg-success-500"
          defaultValue={numberOfYears}
          onValueChange={number => {
            setNumberOfYears(Number(number))
          }}
        />
      </div>
    </div>
  )
}

export default SalaryCalculator
